import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { ProjectAssignButton } from '../project-assign/ProjectAssignButton.js';
import type { CrossDivPmoUiConfig } from '../../../lib/cross-div-pmo-ui.js';

/** @graph-connects none */
export interface ProjectsViewHeaderProps {
  /** 横断 PMO スコープの表示設定（Div バッジ）。全社スコープでは null。 */
  crossDivPmoUi: CrossDivPmoUiConfig | null;
}

/**
 * 全体PJ看板のヘッダー（タイトル + Div バッジ + 右上の「案件をアサインする」）。
 *
 * ボタンは説明文の下端に揃える（`items-end`）。
 *
 * `ProjectsView` から切り出しているのは行数上限（500 行）のため。ヘッダーは状態を持たず
 * `crossDivPmoUi` だけに依存するので、切り出しても props が増えない唯一のブロックだった。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体PJ看板のヘッダー。タイトル・Div バッジ・案件アサインボタンを横並びで表示する
 * @graph-connects ProjectAssignButton [calls] 案件アサインボタン描画（ASSIGN_PROJECT 権限保持者のみ）
 */
export function ProjectsViewHeader({ crossDivPmoUi }: ProjectsViewHeaderProps): ReactNode {
  const { t } = useTranslation();
  return (
    // items-end: ボタンの下端を説明文の下端へ揃える。ボタンは PMO看板と同じ 44px なので、
    // items-start だと見出しの高さに引っ張られて下端が説明文より上で止まる。
    <div className="animate-content-reveal flex items-end justify-between gap-3">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('projects.title')}
          </h1>
          {crossDivPmoUi && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: crossDivPmoUi.divBadgeColor }}
              aria-label={`Division: ${crossDivPmoUi.divLabel}`}
            >
              {crossDivPmoUi.divLabel}
            </span>
          )}
        </div>
        <p className="mt-1 text-[var(--color-text-secondary)]">{t('projects.description')}</p>
      </div>
      {/* アサイン権限（PMOT / PDG の team_leader / 管理者）が無いときは描画されない。 */}
      <ProjectAssignButton />
    </div>
  );
}
